import React from 'react';
import { motion } from 'framer-motion';

const LegalLayout = ({ title, children }) => (
  <section className="py-24 bg-beige-light min-h-screen">
    <div className="max-w-4xl mx-auto px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="prose prose-slate lg:prose-lg max-w-none"
      >
        <h2 className="text-5xl font-serif font-bold text-black mb-12 uppercase tracking-tight">{title}</h2>
        <div className="text-black/80 leading-relaxed space-y-8 font-medium">
          {children}
        </div>
      </motion.div>
    </div>
  </section>
);

export const PrivacyPolicy = () => (
  <LegalLayout title="Privacy Policy">
    <p className="text-accent-orange font-bold uppercase tracking-widest text-xs">Last updated: October 2023</p>
    
    <section>
      <h2 className="text-2xl font-bold text-black mt-8 uppercase tracking-tight">1. Introduction</h2>
      <p>Sandstorm Flix ("we," "our," or "us") respects your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website, sandstormflix.com, including any other media form, media channel, mobile website, or mobile application related or connected thereto.</p>
    </section>

    <section>
      <h2 className="text-2xl font-bold text-black mt-8 uppercase tracking-tight">2. Information We Collect</h2>
      <p>We may collect information about you in a variety of ways. The information we may collect on the Site includes:</p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Personal Data:</strong> Personally identifiable information, such as your name, shipping address, email address, and telephone number, and demographic information, such as your age, gender, hometown, and interests, that you voluntarily give to us when you register with the Site or when you choose to participate in various activities related to the Site.</li>
        <li><strong>Derivative Data:</strong> Information our servers automatically collect when you access the Site, such as your IP address, your browser type, your operating system, your access times, and the pages you have viewed directly before and after accessing the Site.</li>
      </ul>
    </section>

    <section>
      <h2 className="text-2xl font-bold text-black mt-8 uppercase tracking-tight">3. Use of Your Information</h2>
      <p>Having accurate information about you permits us to provide you with a smooth, efficient, and customized experience. Specifically, we may use information collected about you via the Site to:</p>
      <ul className="list-disc pl-6 space-y-2">
        <li>Create and manage your account.</li>
        <li>Email you regarding your account or order.</li>
        <li>Fulfill and manage purchases, orders, payments, and other transactions related to the Site.</li>
        <li>Increase the efficiency and operation of the Site.</li>
        <li>Monitor and analyze usage and trends to improve your experience with the Site.</li>
      </ul>
    </section>

    <section>
      <h2 className="text-2xl font-bold text-black mt-8 uppercase tracking-tight">4. Disclosure of Your Information</h2>
      <p>We may share information we have collected about you in certain situations. Your information may be disclosed as follows: By Law or to Protect Rights; Business Transfers; Third-Party Service Providers; Marketing Communications; Interactions with Other Users; and Online Postings.</p>
    </section>
  </LegalLayout>
);

export const TermsOfService = () => (
  <LegalLayout title="Terms of Service">
    <p className="text-accent-orange font-bold uppercase tracking-widest text-xs">Last updated: October 2023</p>
    
    <section>
      <h2 className="text-2xl font-bold text-black mt-8 uppercase tracking-tight">1. Agreement to Terms</h2>
      <p>These Terms of Service constitute a legally binding agreement made between you, whether personally or on behalf of an entity ("you") and Sandstorm Flix ("we," "us," or "our"), concerning your access to and use of the sandstormflix.com website as well as any other media form, media channel, mobile website or mobile application related, linked, or otherwise connected thereto.</p>
    </section>

    <section>
      <h2 className="text-2xl font-bold text-black mt-8 uppercase tracking-tight">2. Intellectual Property Rights</h2>
      <p>Unless otherwise indicated, the Site and all content, including but not limited to film clips, scripts, images, logos, and trademarks, are our proprietary property and are protected by copyright and trademark laws and various other intellectual property rights and unfair competition laws of the United States, foreign jurisdictions, and international conventions.</p>
    </section>

    <section>
      <h2 className="text-2xl font-bold text-black mt-8 uppercase tracking-tight">3. User Representations</h2>
      <p>By using the Site, you represent and warrant that: (1) all registration information you submit will be true, accurate, current, and complete; (2) you will maintain the accuracy of such information and promptly update such registration information as necessary; (3) you have the legal capacity and you agree to comply with these Terms of Service.</p>
    </section>

    <section>
      <h2 className="text-2xl font-bold text-black mt-8 uppercase tracking-tight">4. Prohibited Activities</h2>
      <p>You may not access or use the Site for any purpose other than that for which we make the Site available. The Site may not be used in connection with any commercial endeavors except those that are specifically endorsed or approved by us.</p>
    </section>

    <section>
      <h2 className="text-2xl font-bold text-black mt-8 uppercase tracking-tight">5. Limitation of Liability</h2>
      <p>In no event will we or our directors, employees, or agents be liable to you or any third party for any direct, indirect, consequential, exemplary, incidental, special, or punitive damages, including lost profit, lost revenue, loss of data, or other damages arising from your use of the site, even if we have been advised of the possibility of such damages.</p>
    </section>
  </LegalLayout>
);

export const AccessibilityStatement = () => (
  <LegalLayout title="Accessibility Statement">
    <p className="text-accent-orange font-bold uppercase tracking-widest text-xs">Last updated: October 2023</p>
    
    <section>
      <h2 className="text-2xl font-bold text-black mt-8 uppercase tracking-tight">Our Commitment</h2>
      <p>Sandstorm Flix is committed to ensuring digital accessibility for people with disabilities. We are continually improving the user experience for everyone and applying the relevant accessibility standards, including the Web Content Accessibility Guidelines (WCAG) 2.1 level AA.</p>
    </section>

    <section>
      <h2 className="text-2xl font-bold text-black mt-8 uppercase tracking-tight">Measures to Support Accessibility</h2>
      <p>Sandstorm Flix takes the following measures to ensure accessibility:
        <ul className="list-disc pl-6 mt-4 space-y-2">
          <li>Include accessibility throughout our internal policies.</li>
          <li>Integrate accessibility into our procurement practices.</li>
          <li>Provide continual accessibility training for our staff.</li>
          <li>Assign clear accessibility targets and responsibilities.</li>
        </ul>
      </p>
    </section>

    <section>
      <h2 className="text-2xl font-bold text-black mt-8 uppercase tracking-tight">Feedback</h2>
      <p>We welcome your feedback on the accessibility of Sandstorm Flix. Please let us know if you encounter accessibility barriers on Sandstorm Flix by contacting us at hello@sandstormflix.com. We try to respond to feedback within 5 business days.</p>
    </section>

    <section>
      <h2 className="text-2xl font-bold text-black mt-8 uppercase tracking-tight">Technical Specifications</h2>
      <p>Accessibility of Sandstorm Flix relies on the following technologies to work with the particular combination of web browser and any assistive technologies or plugins installed on your computer: HTML, WAI-ARIA, CSS, and JavaScript.</p>
    </section>
  </LegalLayout>
);